import { DEFAULT_FILTERS_PAGE_SIZE } from '@/lib/config';
import {
  type FilterOptionsData,
  filterOptionsCache,
} from '@/lib/redux/slices/filters';
import filtersServices from '@/services/filters-services';
import type {
  ReleaseDateRangeInterface,
  YearsRangeInterface,
} from '@/types/api-types';
import type { Coordinates, PercentageCoordinates } from '@/types/pdfTypes';
import { PRE_HYDRATION_LEGISLATION_IN_FORCE_ONLY_DEFAULT } from '@/utils/legislationFilterDefaults';
import { clientSide } from '@/utils/supabase/supabaseClient';

function removeFirstSegment(filePath: string): string {
  return filePath.replace(/^[^\/]+\//, '');
}

function convertToPercentage(
  coordinates: Coordinates,
  page_number: number
): PercentageCoordinates {
  const { points, layout_width, layout_height } = coordinates;

  // Calculate width and height in pixels
  const p0 = points[0];
  const p1 = points[1];
  const p2 = points[2];
  if (!p0 || !p1 || !p2) {
    throw new Error('Invalid coordinates: missing points');
  }
  const width_px = p2[0] - p0[0];
  const height_px = p1[1] - p0[1];

  // Extract left (x) and top (y) positions from the first point
  const left_px = p0[0];
  const top_px = p0[1];

  // Convert pixel values to percentage based on the layout dimensions
  const width_percent = (width_px / layout_width) * 100;
  const height_percent = (height_px / layout_height) * 100;
  const left_percent = (left_px / layout_width) * 100;
  const top_percent = (top_px / layout_height) * 100;

  // Return the result in the desired format
  return {
    height: Number.parseFloat(height_percent.toFixed(2)), // rounded to 2 decimals
    left: Number.parseFloat(left_percent.toFixed(2)), // rounded to 2 decimals
    top: Number.parseFloat(top_percent.toFixed(2)), // rounded to 2 decimals
    width: Number.parseFloat(width_percent.toFixed(2)), // rounded to 2 decimals
    pageIndex: page_number - 1, // Adjust for 0-based index
  };
}

async function getSignedUrl(filePath: string) {
  const supabase = clientSide();

  const { data, error } = await supabase.storage
    .from(process.env['NEXT_PUBLIC_SUPABASE_BUCKET'] ?? '')
    .createSignedUrl(filePath, 60 * 60);

  return { data, error };
}

const getFilters = async (): Promise<FilterOptionsData> => {
  // ✅ Check cache before making API calls (CacheManager handles TTL internally)
  const cacheKey = 'filter-options';
  const cached = filterOptionsCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  // Emergency fallback default - Redux state should already have API defaults loaded
  // This is only used if localStorage is empty AND Redux hasn't initialized yet
  let activeOnlySetting = true;

  // Try to get current value from localStorage to preserve across sessions
  try {
    const storedActiveOnly = localStorage.getItem('legislationActiveOnly');
    if (storedActiveOnly !== null) {
      activeOnlySetting = storedActiveOnly === 'true';
    }
  } catch (error) {
    console.error('Error accessing localStorage:', error);
  }

  // Set up pagination parameters for initial load
  const paginationParams = { page: 1, page_size: DEFAULT_FILTERS_PAGE_SIZE };

  const [
    areas,
    courts,
    judges,
    natures,
    subAreas,
    caseLegislationTypes,
    legislationTypes,
    yearsRange,
    // Issue #908: Fetch decision types with counts for dynamic document count display
    decisionTypes,
    legislationTypesWithCounts,
  ] = await Promise.all([
    filtersServices.getAllAreas(paginationParams),
    filtersServices.getAllCourts(paginationParams),
    filtersServices.getAllJudges(paginationParams),
    filtersServices.getAllNatures(paginationParams),
    filtersServices.getAllSubareas(paginationParams),
    filtersServices.getAllDecisionKinds(paginationParams),
    filtersServices.getAllLegislationTypes(paginationParams),
    filtersServices.getAllYearsRange(),
    // Issue #908: Fetch decision types with document counts
    filtersServices.getAllDecisionTypes(),
    filtersServices.getLegislationTypesWithCounts(),
  ]);

  const result = {
    decisions: {
      areas: areas.data,
      courts: courts.data,
      judges: judges.data,
      natures: natures.data,
      subAreas: subAreas.data,
      caseLegislationTypes: caseLegislationTypes.data.map((item) => ({
        id: item.uuid, // Use uuid as id for consistency with other filters
        name: item.name,
      })),
      // Issue #908: Include decision types with counts for dynamic shimmer display
      decisionTypes: decisionTypes.data,
    },
    legislation: {
      legislationTypes: legislationTypes.data,
      yearRange: yearsRange.data,
      activeOnly: activeOnlySetting, // Use preserved value instead of hardcoded true
      legislationTypesWithCounts: legislationTypesWithCounts.data,
    },
    // Include meta data from API responses
    meta: {
      areas: areas.meta,
      courts: courts.meta,
      judges: judges.meta,
      natures: natures.meta,
      subAreas: subAreas.meta,
      caseLegislationTypes: caseLegislationTypes.meta,
      legislationTypes: legislationTypes.meta,
      // No pagination for yearRange
    },
  };

  // ✅ Store in cache for next time (CacheManager handles timestamp internally)
  filterOptionsCache.set(cacheKey, result);

  return result;
};

/**
 * Interface for filter structure to replace 'any' types
 */
export interface FilterItem {
  id: number;
  name: string;
}

export interface StringFilterItem {
  id: string;
  name: string;
}

interface DecisionFilters {
  areas?: FilterItem[] | undefined;
  courts?: FilterItem[] | undefined;
  judges?: FilterItem[] | undefined;
  natures?: FilterItem[] | undefined;
  subAreas?: FilterItem[] | undefined;
  caseLegislationTypes?: StringFilterItem[] | undefined;
  decisionTypes?: StringFilterItem[] | undefined;
  releaseDateRange?: ReleaseDateRangeInterface | undefined;
  jurisdictions?: string[] | undefined;
  // New orthogonal taxonomy fields
  institutionalCourts?: string[] | undefined;
  publishedOnly?: boolean | undefined;
  [key: string]:
    | (FilterItem | StringFilterItem)[]
    | string[]
    | ReleaseDateRangeInterface
    | boolean
    | undefined;
}

interface LegislationFilters {
  legislationTypes?: FilterItem[] | undefined;
  yearRange?: YearsRangeInterface | undefined;
  activeOnly?: boolean | undefined;
  legislationInForceOnly?: boolean | undefined;
  effectiveAsOf?: string | undefined;
  jurisdictions?: string[] | undefined;
  [key: string]:
    | FilterItem[]
    | string[]
    | undefined
    | YearsRangeInterface
    | boolean
    | string;
}

export interface Filters {
  searchDecisions: boolean;
  searchLegislation: boolean;
  decisions?:
    | {
        areas?: FilterItem[] | undefined;
        courts?: FilterItem[] | undefined;
        judges?: FilterItem[] | undefined;
        natures?: FilterItem[] | undefined;
        subAreas?: FilterItem[] | undefined;
        caseLegislationTypes?: StringFilterItem[] | undefined;
        decisionTypes?: StringFilterItem[] | undefined;
        releaseDateRange?: ReleaseDateRangeInterface | undefined;
        jurisdictions?: string[] | undefined;
        // New orthogonal taxonomy fields
        institutionalCourts?: string[] | undefined;
        publishedOnly?: boolean | undefined;
        [key: string]:
          | FilterItem[]
          | StringFilterItem[]
          | string[]
          | ReleaseDateRangeInterface
          | boolean
          | undefined;
      }
    | undefined;
  legislation?:
    | {
        legislationTypes?: FilterItem[] | undefined;
        yearRange?: YearsRangeInterface | undefined;
        activeOnly?: boolean | undefined;
        legislationInForceOnly?: boolean | undefined;
        effectiveAsOf?: string | undefined;
        jurisdictions?: string[] | undefined;
        [key: string]:
          | FilterItem[]
          | string[]
          | undefined
          | YearsRangeInterface
          | boolean
          | string;
      }
    | undefined;
}

interface CleanedFilters {
  searchDecisions: boolean;
  searchLegislation: boolean;
  decisions?: DecisionFilters | undefined;
  legislation?: LegislationFilters | undefined;
}

/**
 * Removes empty arrays from filter objects to reduce payload size.
 * Only includes filter categories that have selected values.
 *
 * NOTE: This function is still used by @/utils/filterLocalStorage.
 * Do not remove - it's part of the centralized filter storage system.
 *
 * @param filters - The filter object to clean
 * @returns A new filter object with empty arrays removed
 */
function cleanFilters(filters: Filters): CleanedFilters {
  // Start with the main switches for searchDecisions and searchLegislation
  const cleanedFilters: CleanedFilters = {
    searchDecisions: filters.searchDecisions,
    searchLegislation: filters.searchLegislation,
  };

  // Process decision filters if they're being searched
  if (filters.searchDecisions && filters.decisions) {
    const decisionFilters: DecisionFilters = {};
    let hasDecisionFilters = false;

    // NEW: Keep full {id, name} objects instead of extracting IDs
    Object.entries(filters.decisions || {}).forEach(([key, value]) => {
      if (key === 'releaseDateRange' && value) {
        // Handle releaseDateRange as a special case (similar to yearRange for legislation)
        decisionFilters.releaseDateRange = value as ReleaseDateRangeInterface;
        hasDecisionFilters = true;
      } else if (key === 'jurisdictions' && Array.isArray(value)) {
        // Always pass jurisdictions through (even empty = "all jurisdictions" = no filter)
        // Backend parse_filters uses empty_to_none(), so [] means "no filter applied"
        decisionFilters.jurisdictions = value as string[];
        if (value.length > 0) hasDecisionFilters = true;
      } else if (key === 'institutionalCourts' && Array.isArray(value)) {
        // Pass institutionalCourts through when non-empty
        if (value.length > 0) {
          decisionFilters.institutionalCourts = value as string[];
          hasDecisionFilters = true;
        }
      } else if (key === 'publishedOnly' && typeof value === 'boolean') {
        // Only persist publishedOnly=true (false is the default, no need to store)
        if (value === true) {
          decisionFilters.publishedOnly = value;
          hasDecisionFilters = true;
        }
      } else if (Array.isArray(value) && value.length > 0) {
        // All filters now keep full {id, name} objects
        decisionFilters[key] = value as (FilterItem | StringFilterItem)[];
        hasDecisionFilters = true;
      }
    });

    // Only add the decisions object if it has any filters
    if (hasDecisionFilters) {
      cleanedFilters.decisions = decisionFilters;
    }
  }

  // Process legislation filters if they're being searched
  if (filters.searchLegislation) {
    const legislationFilters: LegislationFilters = {};

    // Emergency fallback default - should normally come from Redux state (which gets it from API)
    let activeOnlyValue = true;

    if (filters.legislation?.activeOnly !== undefined) {
      // Handle case where activeOnly might be an array
      if (Array.isArray(filters.legislation.activeOnly)) {
        if (filters.legislation.activeOnly.length > 0) {
          // Convert to boolean - if it's an array with values, take the first one
          activeOnlyValue = Boolean(filters.legislation.activeOnly[0]);
        }
      } else {
        // It's already a boolean or can be converted to one
        activeOnlyValue = Boolean(filters.legislation.activeOnly);
      }
    }

    legislationFilters.activeOnly = activeOnlyValue;

    // Handle legislationInForceOnly boolean (same pattern as activeOnly)
    // Emergency fallback default - should normally come from Redux state (which gets it from API).
    // See PRE_HYDRATION_LEGISLATION_IN_FORCE_ONLY_DEFAULT for why a literal is unavoidable here (#7652).
    let legislationInForceOnlyValue =
      PRE_HYDRATION_LEGISLATION_IN_FORCE_ONLY_DEFAULT;

    if (filters.legislation?.legislationInForceOnly !== undefined) {
      if (Array.isArray(filters.legislation.legislationInForceOnly)) {
        if (filters.legislation.legislationInForceOnly.length > 0) {
          legislationInForceOnlyValue = Boolean(
            filters.legislation.legislationInForceOnly[0]
          );
        }
      } else {
        legislationInForceOnlyValue = Boolean(
          filters.legislation.legislationInForceOnly
        );
      }
    }

    legislationFilters.legislationInForceOnly = legislationInForceOnlyValue;

    // Process other legislation filters if they exist
    if (filters.legislation) {
      // NEW: Keep full {id, name} objects for legislationTypes
      Object.entries(filters.legislation || {}).forEach(([key, value]) => {
        if (key === 'yearRange' && value) {
          // Handle yearRange as a special case
          legislationFilters.yearRange = value as YearsRangeInterface;
        } else if (key === 'effectiveAsOf' && value) {
          legislationFilters.effectiveAsOf = value as string;
        } else if (key === 'activeOnly') {
          // Skip - already handled above with proper boolean normalization
          // This prevents overwriting the sanitized activeOnlyValue with raw input
          // that might be an array like [true] instead of boolean true
        } else if (key === 'legislationInForceOnly') {
          // Skip - already handled above with proper boolean normalization
        } else if (
          key === 'legislationTypes' &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          // NEW: Keep full {id, name} objects instead of extracting IDs
          legislationFilters.legislationTypes = value as FilterItem[];
        } else if (key === 'jurisdictions' && Array.isArray(value)) {
          // Always pass jurisdictions through (even empty = "all jurisdictions" = no filter)
          // Backend parse_filters uses empty_to_none(), so [] means "no filter applied"
          legislationFilters.jurisdictions = value as string[];
        }
      });
    }

    // Always add the legislation object since we have at least the activeOnly filter
    cleanedFilters.legislation = legislationFilters;
  }

  return cleanedFilters;
}

// Re-export from @/lib/utils to avoid circular dependency:
// this file imports filters-services → ApiService (client-only),
// but normalizeText is used by @/lib/utils which must be SSR-safe.
export { normalizeText } from '@/lib/utils';

export {
  cleanFilters,
  convertToPercentage,
  getFilters,
  getSignedUrl,
  removeFirstSegment,
};
